const crypto = require("crypto");
import cx from "classnames";
import React, { useState } from "react";
import JobPost from "~/models/job-post";
import ChevronDown from "~/views/identity/chevron-down";

// マークダウンの装飾記法を除去する関数
function stripMarkdownFormatting(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/__(.*?)__/g, "$1")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/__(.*?)__/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/_(.*?)_/g, "$1")
    .replace(/`(.*?)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/~~(.*?)~~/g, "$1")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const TableOfContents = ({
  jobPost,
  className,
  currentSection,
  onSelect,
}: {
  jobPost: JobPost;
  className?: string;
  currentSection?: string;
  onSelect?: (id: string) => void;
}) => {
  const [collapsed, setCollapsed] = useState(true);

  const headings: any = [];
  let pointer = -1;

  if (jobPost.content) {
    // Parse markdown content to extract headings
    const markdownContent = jobPost.content;
    const lines = markdownContent.split("\n");

    lines.forEach((line: string) => {
      const trimmedLine = line.trim();

      // Check for H2 headings (## Title)
      if (trimmedLine.startsWith("## ") && !trimmedLine.startsWith("### ")) {
        const title = stripMarkdownFormatting(
          trimmedLine.replace(/^## /, "").trim()
        );
        if (title) {
          pointer += 1;
          headings[pointer] = {
            title: title,
            hash: crypto.createHash("md5").update(title).digest("hex"),
            items: [],
          };
        }
      }

      // Check for H3 headings (### Title)
      if (trimmedLine.startsWith("### ") && pointer >= 0) {
        const title = stripMarkdownFormatting(
          trimmedLine.replace(/^### /, "").trim()
        );
        if (title) {
          headings[pointer].items.push({
            title: title,
            hash: crypto.createHash("md5").update(title).digest("hex"),
          });
        }
      }
    });
  }

  if (!jobPost.isAutoGenerated) {
    headings.push({
      title: "会社概要",
      hash: "company",
      items: [],
    });
  }

  if (jobPost.links.length > 0) {
    headings.push({
      title: `${jobPost.company.name}での仕事についてもっと知る`,
      hash: "links",
      items: [],
    });
  }

  if (jobPost.relatedArticles.length > 0) {
    headings.push({
      title: "関連記事",
      hash: "articles",
      items: [],
    });
  }

  function onClick(hash: string, e: React.MouseEvent<HTMLElement>) {
    e.preventDefault();

    var header = document.getElementById("navigation")!;
    var target = document.getElementById(hash)!;

    var offsetPosition =
      target.getBoundingClientRect().top +
      window.scrollY -
      20 -
      header.getBoundingClientRect().height;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });

    if (onSelect) {
      onSelect(hash);
    }

    history.pushState({}, "", `#${hash}`);
  }

  function onToggle(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    setCollapsed(!collapsed);
  }

  return (
    <nav
      className={cx("overflow-hidden bg-neutral-99 rounded-lg", className, {
        "h-[62px] w1180:h-auto": collapsed,
      })}>
      <div>
        <button
          className="px-4 py-5 flex justify-between items-center w-full"
          onClick={(e) => onToggle(e)}>
          <h2 className="font-semibold text-sm text-neutral-60 leading-[22px]">
            目次
          </h2>
          <ChevronDown
            className={cx("w1180:hidden", {
              "rotate-180": !collapsed,
            })}
          />
        </button>

        <ol className="list-none px-4 pb-5">
          {headings.map((heading: any, index: number) => {
            return (
              <React.Fragment key={heading.hash}>
                <li className="mb-4 last:mb-0 leading-[22px]">
                  <a
                    href={`#${heading.hash}`}
                    className="flex"
                    onClick={(e) => onClick(heading.hash, e)}>
                    <React.Fragment>
                      <span
                        className={cx(
                          "mr-3 font-bold text-neutral-60 text-sm block w-3",
                          {
                            "text-cyan-strong": heading.hash == currentSection,
                          }
                        )}>
                        {`${index + 1}.`}
                      </span>
                      <span
                        className={cx(
                          "font-semibold text-neutral-30 text-sm hover:underlined-link",
                          {
                            "text-cyan-strong": heading.hash == currentSection,
                          }
                        )}>
                        {heading.title}
                      </span>
                    </React.Fragment>
                  </a>

                  <ul className="list-none pl-6">
                    {heading.items.map((item: any) => (
                      <li
                        key={item.hash}
                        className={cx(
                          "mt-3 text-xs text-neutral-30 leading-[22px] hover:underlined-link",
                          {
                            "text-cyan-strong": item.hash == currentSection,
                          }
                        )}>
                        <a
                          href={`#${item.hash}`}
                          onClick={(e) => onClick(item.hash, e)}>
                          {item.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              </React.Fragment>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

export default TableOfContents;
